import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Info,
  Package,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { appNumberLocale } from "./formatting";
import { useLang } from "./i18n-binding";
import type { Tone, ToneSpec } from "@pervasivemind/bi-components";
import {
  AnomalyCard as BiAnomalyCard,
  InfoTip,
  KPIBar as BiKPIBar,
  KPICard as BiKPICard,
  KPICompact as BiKPICompact,
  KPICompare as BiKPICompare,
  KPICore as BiKPICore,
  KPIHero as BiKPIHero,
  KPIIconStat as BiKPIIconStat,
  KPIRing as BiKPIRing,
  KPISparkline as BiKPISparkline,
  PALETTE,
  ScoreCard as BiScoreCard,
} from "@pervasivemind/bi-components";

interface KPICardProps {
  title: string;
  /**
   * The display value. Display-only variants (card / compact / hero /
   * iconstat / sparkline) may receive a React node — `DashboardBody`
   * passes a `NumberTween` there (SWR PR-B). The numeric variants
   * (ring / bar / anomaly / score / compare) parse `String(value)` to
   * derive `numericValue`, so they MUST keep receiving a string/number
   * (a node would read as `[object Object]` → 0); the gate lives in
   * `DashboardBody`'s `KPI_TWEEN_VARIANTS`. When `numericValue` is set,
   * the numeric variants read it instead of parsing `value`.
   */
  value: string | number | ReactNode;
  subtitle?: string;
  /**
   * Tailwind border class — accepts either left-only (`border-l-primary`)
   * or generic (`border-blue-500`); the latter is rewritten to a
   * left-only variant before being passed to the underlying component.
   * Defaults to `border-l-accent` (brand magenta, redesign v2).
   */
  accentColor?: string;
  /**
   * Filter keys this KPI ignores (from `props.ignore_filters`). When
   * non-empty, a small "i" badge appears in the card corner with the
   * list in a tooltip.
   */
  ignoredFilters?: ReadonlyArray<string>;

  // ── Phase A: variant + per-variant extras ─────────────────
  /**
   * Visual variant — the entire dispatch hangs on this. Defaults to
   * `'card'` for backward compatibility with reports that pre-date the
   * Phase A migration.
   */
  variant?:
    | "card"
    | "sparkline"
    | "ring"
    | "bar"
    | "hero"
    | "compare"
    | "compact"
    | "stacked"
    | "iconstat"
    | "anomaly"
    | "score"
    | "core";
  /** Max value for ring/bar/score variants (default 100). */
  max?: number;
  /** Gradient classes for hero variant, e.g. "from-primary to-primary-ink". */
  gradient?: string;
  /** Icon slug for hero/iconstat variants — mapped to a Lucide icon below. */
  icon?: string;
  /** Tailwind color word for iconstat icon background, e.g. "primary"/"accent"/"emerald". */
  iconBg?: string;
  /** Sparkline numeric series — used by the `sparkline` variant. */
  sparkData?: ReadonlyArray<number>;
  /** Previous period value — used by the `compare` variant. */
  previous?: number;
  /** Raw numeric KPI value in the SAME scale as `previous` (percent ×100).
   *  When set, the numeric variants read THIS instead of re-parsing the
   *  display string — the display string follows the active locale
   *  (es-AR "1.500"), and parsing it back as en-US reads 1.5. */
  numericValue?: number;
  /** Pre-computed delta % — used by the `compact` variant when wanted. */
  change?: number;
  /** Original KPI ``format`` from the dashboard config; forwarded to
   * ``KPICompare`` so percent variants render "62.0%" / "+0.5 pp". */
  format?: "number" | "percent" | "currency";
  /** Suffix appended to current/previous (e.g. " sem."). */
  unit?: string;

  // ── Phase H: anomaly variant ──
  /** Severity level for `anomaly` variant. Defaults to `warning`. */
  severity?: "critical" | "warning" | "info" | string;
  /** Static descriptive blurb shown beneath value for `anomaly`. */
  description?: string;
  /** Expected/baseline value for `anomaly` — auto-computes deviation %. */
  expected?: number;

  // ── Phase H: score variant ──
  /** Per-axis breakdown rows for `score` variant — each renders as a 0–100% bar. */
  breakdown?: ReadonlyArray<{ label: string; value: number }>;

  // ── Phase I (drill): click-to-expand support ──
  /** When set, the KPI becomes clickable and ``onDrillClick`` fires with this id. */
  drillTargetId?: string;
  /** Visual state — true when this KPI's drill is currently open. */
  isActive?: boolean;
  onDrillClick?: (drillId: string) => void;

  // ── v1 finalization (iter 16): goal direction inversion ──
  /**
   * Semantics of "improvement" for the ``compare`` variant.
   * ``"up"`` (default) — bigger is better; the lib's default
   * coloring (green for current > previous) applies.
   * ``"down"`` — smaller is better (e.g. % of unauthorised sellers,
   * mean expulsion time). Renders ``InvertedCompareCard`` so the
   * red/green mapping flips. Set by ``goal_direction`` in the
   * dashboard config.
   */
  goalDirection?: "up" | "down";

  /**
   * Pre-resolved tooltip text (config ``info``, localized by the
   * caller). Renders an ``InfoTip`` ⓘ pinned to the card's top-right —
   * the plain-language explanation of what this KPI measures.
   */
  info?: string;

  /**
   * Epic v3 (scorecards) — threshold bands that tint the VALUE, and
   * the value they band. Opt-in: with no bands the card renders
   * byte-identically to before, because a bare number must never imply
   * a colour (the value-based cell styling this codebase deleted).
   *
   * This is the THRESHOLD axis. The card's top border and delta chip
   * keep using the separate 3-state `Sentiment` (the sign of a delta).
   */
  valueTone?: ToneSpec;
  valueToneValue?: number;
  /** Pre-resolved coloured caption line under the value. */
  caption?: { text: string; tone?: Tone };
  /** Bottom action band. Without `href` it renders DISABLED (dashed,
   *  muted, not focusable) instead of as a live button — a button that
   *  navigates nowhere teaches the reader to distrust the page. Costs
   *  ~30px, so a KPI declaring one usually needs one more row unit. */
  action?: { label: string; href?: string; note?: string; onClick?: () => void };
  // ── DSA KpiStrip (`core` variant) ──
  /** Small pill pinned top-right ("CORE"). The strip hides it while
   *  another KPI is focused (one focus at a time). */
  badge?: string;
  /** Signature colour (hex) for the left border, wash, badge and ring. */
  color?: string;
  /** Focus ring — the strip passes it for the KPI whose drill is open. */
  focused?: boolean;
  /** Operator drawn in the gap BEFORE the card ("×", "="), centred on
   *  the strip's 12px column gap. Hidden below `md`, where the strip
   *  stacks and there is no gap to sit in. */
  joiner?: string;
}

/** Variants that parse `String(value)` into a number internally — a
 *  non-scalar `value` here means fabricated data (review #56 #11). */
const NUMERIC_VALUE_VARIANTS: ReadonlySet<string> = new Set([
  "ring",
  "bar",
  "anomaly",
  "score",
  "compare",
  "core",
]);

/**
 * Wrapper over the eight `@pervasivemind/bi-components` KPI variants.
 *
 * Dispatch table (Phase A):
 *
 *   | variant     | bi-components component | extras read from props          |
 *   |-------------|--------------------|----------------------------------|
 *   | card (def)  | KPICard            | accent                            |
 *   | compact     | KPICompact         | change, color                     |
 *   | stacked     | KPICompact stacked | change, color (label over value)  |
 *   | ring        | KPIRing            | max, displayValue                 |
 *   | bar         | KPIBar             | max, color, displayValue           |
 *   | hero        | KPIHero            | gradient, icon                    |
 *   | iconstat    | KPIIconStat        | icon, iconBg                      |
 *   | sparkline   | KPISparkline       | sparkData (else falls back to card) |
 *   | compare     | KPICompare         | previous (else falls back to card)  |
 *   | core        | KPICore            | previous?, badge, color, description (DSA strip) |
 *
 * For sparkline + compare the extras come from the backend's KPI response
 * (executed via `sparkline_sql` / `previous_sql`). Until that backend
 * support lands, the wrapper renders a basic `KPICard` so the editor
 * doesn't crash.
 *
 * Every variant gets the `ignoredFilters` info badge in the corner when
 * the KPI explicitly drops filters from its query bucket.
 */
/** Variants that draw the ⓘ / "ignores filters" icons in their own
 *  title row (`trailing`) instead of the absolute corner overlay. */
const INLINE_ICON_VARIANTS: ReadonlySet<string> = new Set(["core", "compact", "stacked"]);

export function KPICard({
  title,
  value,
  subtitle,
  accentColor,
  ignoredFilters,
  variant,
  max,
  gradient,
  icon,
  iconBg,
  sparkData,
  previous,
  numericValue: numericValueProp,
  change,
  format,
  unit,
  severity,
  description,
  expected,
  breakdown,
  drillTargetId,
  isActive,
  onDrillClick,
  goalDirection,
  info,
  valueTone,
  valueToneValue,
  caption,
  action,
  badge,
  color,
  focused,
  joiner,
}: KPICardProps): JSX.Element {
  // i18n strings rendered inside the drill footer + the ``ignored
  // filters`` info tooltip. Each consumer must register the keys in
  // its own EN+ES dict (see ai-reporting-builder/.../i18n/index.ts
  // and hp-portal/.../i18n/index.ts). Falls back to English when a
  // key is missing.
  const { t } = useTranslation();
  const lang = useLang();
  const accent = mapAccent(accentColor);
  const hasIgnored = ignoredFilters && ignoredFilters.length > 0;
  const v = variant || "card";

  // `core` lays its badge out in a header flex row, and `compact` puts
  // its title and value on one line: in both the ⓘ / "ignores
  // filters" icons go INTO the title row (`trailing`) instead of the
  // absolute corner overlay every other variant uses — pinned at
  // `right-2 top-2` they sat on top of the CORE pill.
  const cornerIcons =
    info || (ignoredFilters && ignoredFilters.length > 0) ? (
      <>
        {info && (
          <span className="pointer-events-auto text-neutral-400">
            <InfoTip text={info} />
          </span>
        )}
        {ignoredFilters && ignoredFilters.length > 0 && (
          <span
            className="pointer-events-auto text-neutral-300 hover:text-neutral-500"
            title={t("kpiCard.ignoredFilters", {
              defaultValue: "Ignores filters: {filters}",
              filters: ignoredFilters.join(", "),
            })}
          >
            <Info size={12} />
          </span>
        )}
      </>
    ) : undefined;

  // Loud guard (review #56 #11): the numeric variants derive
  // `numericValue` from `String(value)` — a React node there (e.g. a
  // NumberTween that leaked past DashboardBody's KPI_TWEEN_VARIANTS
  // gate) stringifies to "[object Object]" and silently renders 0.
  // That is invented data; scream instead of hiding it.
  if (
    NUMERIC_VALUE_VARIANTS.has(v) &&
    typeof value !== "string" &&
    typeof value !== "number"
  ) {
    // eslint-disable-next-line no-console
    console.error(
      `[dashboard-render] KPICard variant "${v}" requires a string/number ` +
        `value but received ${
          value === null ? "null" : typeof value
        } — numeric parsing would fabricate 0. Check the KPI_TWEEN_VARIANTS ` +
        `gate in DashboardBody (title: "${title}").`,
    );
  }

  // Numeric display value derived from `value` for ring/bar variants
  // that take a numeric `value` separately from the displayed string.
  //
  // MISSING IS NOT ZERO. `formatCellValue` renders a null KPI as "—",
  // and the old `… || 0` turned that into a real 0: a ring drawn at 0%,
  // a bar at empty, a score of zero — all of them indistinguishable
  // from a measured zero. That is fabricated data, the one thing this
  // product may never do. `parsedValue` is null when there is nothing
  // to draw, and the numeric variants fall back to the plain card,
  // which shows the "—" as text.
  // Gate on the STRIPPED string, not on Number(): `Number("")` is 0,
  // not NaN, so checking Number.isFinite on the parse of "—" would
  // happily report a finite zero — the very bug this guard exists to
  // stop.
  const numericSource =
    numericValueProp !== undefined
      ? String(numericValueProp)
      : typeof value === "number"
        ? String(value)
        : String(value ?? "").replace(/[^\d.-]/g, "");
  const hasNumericValue =
    numericSource.trim() !== "" && Number.isFinite(Number(numericSource));
  const numericValue = hasNumericValue ? Number(numericSource) : 0;

  // A numeric variant with no number renders as the plain card so the
  // reader sees "—" instead of a confident zero.
  const effectiveVariant =
    NUMERIC_VALUE_VARIANTS.has(v) && !hasNumericValue ? "card" : v;

  let body: ReactNode;
  switch (effectiveVariant) {
    case "compact":
    case "stacked":
      // `stacked` = the same tile with the label on top and the value
      // below; `compact` keeps label and value on one line.
      body = (
        <BiKPICompact
          title={title}
          value={value}
          change={change}
          color={PALETTE[0]}
          trailing={cornerIcons}
          stacked={effectiveVariant === "stacked"}
        />
      );
      break;

    case "ring": {
      // Always show the percentage as the display value — `${num}%`
      // is more legible than the raw aggregate text on a 0-100 ring.
      // When `value` already comes pre-formatted with a `%` we trust it.
      const ringMax = max ?? 100;
      const ringDisplay =
        typeof value === "string" && value.includes("%")
          ? value
          : `${Math.round(numericValue)}%`;
      body = (
        <BiKPIRing
          title={title}
          value={numericValue}
          max={ringMax}
          displayValue={ringDisplay}
          subtitle={subtitle}
          color={PALETTE[0]}
        />
      );
      break;
    }

    case "bar": {
      // Display "{num}% of {max}" so a value of 0 reads as "0% of 100"
      // instead of an isolated "0" sandwiched between the bar's own
      // 0..max axis labels (which the lib already renders).
      const barMax = max ?? 100;
      const barDisplay =
        typeof value === "string" && value.includes("%")
          ? value
          : `${Math.round(numericValue)}% of ${barMax}`;
      body = (
        <BiKPIBar
          title={title}
          value={numericValue}
          max={barMax}
          color={PALETTE[0]}
          displayValue={barDisplay}
        />
      );
      break;
    }

    case "hero":
      body = (
        <BiKPIHero
          title={title}
          value={value}
          subtitle={subtitle}
          gradient={gradient}
          icon={icon ? renderIcon(icon, "white") : undefined}
        />
      );
      break;

    case "iconstat": {
      const bg = iconBg || "accent";
      body = (
        <BiKPIIconStat
          title={title}
          value={value}
          subtitle={subtitle}
          iconColor={`text-${bg}`}
          iconBg={`bg-${bg}-50`}
          icon={icon ? renderIcon(icon, "currentColor") : undefined}
        />
      );
      break;
    }

    case "sparkline":
      // Falls back to plain KPICard when no spark series — backend may
      // not be wired yet for sparkline_sql; we want the editor to still
      // show something useful.
      if (sparkData && sparkData.length > 0) {
        body = (
          <BiKPISparkline
            title={title}
            value={value}
            sparkData={[...sparkData]}
            color={PALETTE[0]}
          />
        );
      } else {
        body = (
          <BiKPICard
            title={title}
            value={value}
            subtitle={subtitle ?? "Set sparkline_sql to enable"}
            accent={accent}
          />
        );
      }
      break;

    case "compare":
      // Same fallback strategy — no `previous` → render as basic card.
      if (typeof previous === "number") {
        // v1 finalization (iter 16) + F3 redesign: when
        // ``goalDirection === "down"`` bigger value = worse (more
        // unauthorised sellers, longer expulsion time, …) so the lib's
        // default "up=good" sentiment is wrong. Since F3 the lib's
        // ``KPICompare`` accepts an explicit ``sentiment`` override, so
        // the local ``InvertedCompareCard`` mirror was retired — both
        // directions now render the SAME component (sentiment top
        // border + semantic delta chip). Epsilon: ~0.05% relative
        // change reads as flat/neutral (same threshold the old
        // inverted mirror used).
        if (goalDirection === "down") {
          const delta = numericValue - previous;
          const epsilon = Math.abs(previous) * 0.0005;
          const sentiment =
            Math.abs(delta) <= epsilon
              ? ("neutral" as const)
              : delta < 0
                ? ("good" as const)
                : ("bad" as const);
          body = (
            <BiKPICompare
              title={title}
              current={numericValue}
              previous={previous}
              format={format === "percent" ? "percent" : "number"}
              unit={unit}
              sentiment={sentiment}
              valueTone={valueTone}
              valueToneValue={valueToneValue}
              caption={caption}
            />
          );
        } else {
          body = (
            <BiKPICompare
              title={title}
              current={numericValue}
              previous={previous}
              format={(format === "percent" ? "percent" : "number") as never}
              unit={unit as never}
              valueTone={valueTone}
              valueToneValue={valueToneValue}
              caption={caption}
            />
          );
        }
      } else {
        body = (
          <BiKPICard
            title={title}
            value={value}
            subtitle={subtitle ?? "Set previous_sql to enable"}
            accent={accent}
          />
        );
      }
      break;

    case "core": {
      // DSA strip card. `previous` is optional here (no chip without it);
      // `goalDirection === "down"` flips the delta sentiment exactly like
      // the `compare` variant above.
      let coreSentiment: "good" | "bad" | "neutral" | undefined;
      if (goalDirection === "down" && typeof previous === "number") {
        const delta = numericValue - previous;
        const epsilon = Math.abs(previous) * 0.0005;
        coreSentiment =
          Math.abs(delta) <= epsilon ? "neutral" : delta < 0 ? "good" : "bad";
      }
      body = (
        <BiKPICore
          title={title}
          current={numericValue}
          previous={typeof previous === "number" ? previous : undefined}
          format={format === "percent" ? "percent" : "number"}
          unit={unit}
          subtitle={description ?? subtitle}
          badge={badge}
          color={color}
          focused={focused ?? isActive}
          sentiment={coreSentiment}
          valueTone={valueTone}
          valueToneValue={valueToneValue}
          caption={caption}
          action={action}
          trailing={cornerIcons}
          locale={appNumberLocale()}
          previousLabel={t("dashboard.kpi.previous")}
        />
      );
      break;
    }

    case "anomaly": {
      // Phase H — value with severity badge + optional expected baseline
      // and free-text description. Severity drives the colour ramp on
      // the bi-components component.
      const sev =
        severity === "critical" || severity === "warning" || severity === "info"
          ? severity
          : "warning";
      body = (
        <BiAnomalyCard
          title={title}
          value={numericValue}
          expected={typeof expected === "number" ? expected : undefined}
          severity={sev}
          description={description}
          locale={lang}
          strings={{ expected: t("dashboard.kpi.anomalyExpected") }}
        />
      );
      break;
    }

    case "score":
      // Phase H — score 0..max ring + per-axis breakdown bars. When the
      // breakdown SQL hasn't been wired the bars area is empty — the
      // ring still renders so the editor never looks broken.
      body = (
        <BiScoreCard
          title={title}
          score={numericValue}
          maxScore={max ?? 100}
          breakdown={breakdown ? [...breakdown] : undefined}
          color={PALETTE[0]}
        />
      );
      break;

    case "card":
    default:
      body = (
        <BiKPICard
          title={title}
          value={value}
          subtitle={subtitle}
          accent={accent}
          valueTone={valueTone}
          valueToneValue={valueToneValue}
          caption={caption}
          action={action}
        />
      );
  }

  // `h-full` stretches the wrapper to the row height set by its grid
  // parent; `[&>*:first-child]:h-full` propagates that to the variant
  // body so cards / rings / hero / sparkline all align even when their
  // intrinsic content height differs (a `compact` row is much shorter
  // than a `hero` gradient card by default). `min-h-[76px]` is the
  // floor so a row of mixed variants doesn't collapse smaller than a
  // standard card when no other variant is taller — was 110px, reduced
  // because the empty whitespace inside basic ``card`` variant tiles
  // (which only render label + value, ~46px of content) made KPI rows
  // visually float in air.
  //
  // `compact` is the ONE variant the floor must not reach. It is the
  // single-line tile — label left, value right — and being short is the
  // whole of what it is for. The floor was written to stop a row of
  // MIXED variants from collapsing below a standard card; applied to
  // `compact` it does the opposite, inflating a 36px row into a 76px
  // box of air and making a declared `height: 1` (a 40px cell) impossible
  // to honour: the config asked for 40 and the component answered 76, so
  // the tile overflowed its cell and painted over the one below it.
  // See hp-portal/.../KPICard.tsx for the rationale behind the drillable
  // styling — we deliberately avoid ``ring-*`` to prevent the doubled
  // border effect with the inner bi-components card.
  const isDrillable = Boolean(drillTargetId && onDrillClick);
  const handleClick = isDrillable && drillTargetId && onDrillClick
    ? () => onDrillClick(drillTargetId)
    : undefined;
  const wrapperClass = [
    "group relative h-full rounded-[14px] [&>*:first-child]:h-full",
    v === "compact" ? "" : "min-h-[76px]",
    isDrillable
      ? "cursor-pointer transition-[transform,box-shadow] duration-150 transform-gpu motion-safe:hover:-translate-y-0.5"
      : "",
    isDrillable && !isActive
      ? "hover:shadow-[0_8px_24px_-14px_rgba(229,25,125,0.45)]"
      : "",
    isActive
      ? "bg-accent/[0.06] shadow-[0_10px_32px_-12px_rgba(229,25,125,0.5)] -translate-y-0.5"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={wrapperClass}
      onClick={handleClick}
      role={isDrillable ? "button" : undefined}
      tabIndex={isDrillable ? 0 : undefined}
      aria-pressed={isDrillable ? isActive : undefined}
      onKeyDown={
        isDrillable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick?.();
              }
            }
          : undefined
      }
    >
      {/* `[&>*]:h-full` is the level the wrapper's own
          `[&>*:first-child]:h-full` cannot reach: that selector stops
          at THIS div, and the variant body is one deeper. Without it a
          short variant (a plain `card` with no Prev line) renders at
          its intrinsic height inside a stretched cell, so a strip that
          mixes `card` and `compare` reads ragged — which is exactly
          what the grid's equal-row promise is supposed to prevent. */}
      <div
        className={`${isDrillable ? "h-full pb-6" : "h-full"} [&>*]:h-full`}
      >
        {body}
      </div>
      {info && !INLINE_ICON_VARIANTS.has(effectiveVariant) && (
        <span className="pointer-events-auto absolute right-2 top-2 z-10 text-neutral-400">
          <InfoTip text={info} />
        </span>
      )}
      {hasIgnored && !INLINE_ICON_VARIANTS.has(effectiveVariant) && (
        <span
          className={`pointer-events-auto absolute ${info ? "right-7" : "right-2"} top-2 z-10 text-neutral-300 hover:text-neutral-500`}
          title={t("kpiCard.ignoredFilters", {
            defaultValue: "Ignores filters: {filters}",
            filters: ignoredFilters!.join(", "),
          })}
        >
          <Info size={12} />
        </span>
      )}
      {isActive && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-3 bottom-[26px] h-[2px] rounded-full bg-accent"
        />
      )}
      {/* After the body on purpose: the wrapper stretches its FIRST child
          (`[&>*:first-child]:h-full`), which must stay the card. */}
      {joiner && (
        <span
          aria-hidden="true"
          data-kpi-joiner={joiner}
          className="pointer-events-none absolute -left-[17px] top-1/2 z-20 hidden h-[22px] w-[22px] -translate-y-1/2 items-center justify-center rounded-full border border-neutral-200 bg-white text-[13px] font-bold leading-none text-neutral-500 shadow-sm md:flex"
        >
          {joiner}
        </span>
      )}
      {isDrillable && (
        <span
          className={[
            "pointer-events-none absolute bottom-1.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full px-2 py-0.5",
            "text-2xs font-semibold transition-colors",
            isActive
              ? "bg-accent text-white shadow-sm"
              : "text-neutral-600 group-hover:text-accent-dark group-hover:bg-accent/10",
          ].join(" ")}
        >
          {isActive ? (
            <>
              <ChevronUp size={11} strokeWidth={2.5} />
              {t("kpiCard.closeAnalysis", {
                defaultValue: "Close analysis",
              })}
            </>
          ) : (
            <>
              <ChevronDown size={11} strokeWidth={2.5} />
              {t("kpiCard.openAnalysis", {
                defaultValue: "Open analysis",
              })}
            </>
          )}
        </span>
      )}
    </div>
  );
}

function mapAccent(input: string | undefined): string {
  if (!input) return "border-l-accent";
  if (input.startsWith("border-l-")) return input;
  return input.replace(/^border-/, "border-l-");
}

/**
 * Map a slug (persisted in `props.icon`) to a Lucide icon element.
 * Keep this list small + curated — analysts pick from a dropdown of
 * exactly these slugs in `KpiEditor`.
 */
function renderIcon(slug: string, color: string): ReactNode {
  const stroke = color === "white" ? "white" : "currentColor";
  const props = {
    width: 22,
    height: 22,
    strokeWidth: 2,
    stroke,
    fill: "none" as const,
  };
  switch (slug) {
    case "alert":
      return <AlertTriangle {...props} />;
    case "trending":
      return <TrendingUp {...props} />;
    case "dollar":
      return <DollarSign {...props} />;
    case "users":
      return <Users {...props} />;
    case "package":
      return <Package {...props} />;
    case "check":
      return <CheckCircle {...props} />;
    case "zap":
      return <Zap {...props} />;
    case "target":
      return <Target {...props} />;
    default:
      return null;
  }
}

// NOTE (F3 redesign): the former ``InvertedCompareCard`` local mirror
// was removed — inverted ("down is good") compare KPIs now render the
// lib's ``KPICompare`` with an explicit ``sentiment`` override (see the
// ``compare`` case above), so both directions share one visual
// treatment (semantic top border + ▲/▼ delta chip).
